import StatusChip from 'components/@extended/StatusChip';
import { BoxNowrap } from 'components/misc/BoxNoWrap';
import ListClamp from 'components/misc/ListClamp';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Tooltip } from 'components/ui/tooltip';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { getInvoiceStatusChipType, getInvoiceTypeLetter } from 'pages/invoices/util/invoiceUtil';
import { getLinks } from 'pages/matters/util';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { LinkedInvoiceMinDto, PrepaymentListItem } from 'types/prepayments';
import { cn } from 'utils/cn';
import { getClassifierText } from 'utils/getTranslations';

/** A link in a cell: the cell's `.entity-link` rule colours it, and it underlines on hover. */
const CELL_LINK = 'entity-link w-fit no-underline hover:underline';

/** The invoice type as its one-letter chip, with the full type name in a tooltip. */
const InvoiceTypeLetterChip = ({ typeCode, typeLabel }: { typeCode: string; typeLabel: string }) => (
    <div className="text-fg-secondary new:pr-0 pr-4">
        <Tooltip content={typeLabel} className="w-fit text-center">
            <div className="w-fit">
                <StatusChip label={getInvoiceTypeLetter(typeCode)} type="neutral" />
            </div>
        </Tooltip>
    </div>
);

export const PrepaymentNumberClientPayer = ({
    prepayment,
    classifiers,
}: {
    prepayment: PrepaymentListItem;
    classifiers: Record<string, ClassifierData>;
}): JSX.Element => {
    const { id, invoiceNumber, clientParty, invoiceMattersClientParties, invoiceStatusTypeCode } = prepayment;

    const clientLinks = getLinks('contact', invoiceMattersClientParties ?? [], {
        showPartyNumber: true,
        useLinkWithOrigin: true,
    });

    const hasDifferingInvoiceClient =
        (invoiceMattersClientParties?.length ?? 0) > 1 ||
        ((invoiceMattersClientParties?.length ?? 0) === 1 &&
            invoiceMattersClientParties?.at(0)?.id !== clientParty?.id);

    return (
        <div className="flex flex-col">
            <LinkWithOrigin className={cn(CELL_LINK, 'font-semibold whitespace-normal')} to={`/invoice/${id}`}>
                {invoiceNumber}
            </LinkWithOrigin>
            {/* The new design keeps the chip on the cell's bottom line. */}
            <div className="new:order-last mt-0.5">
                <StatusChip
                    label={getClassifierText(classifiers?.['INVOICE_STATUS_TYPE'], invoiceStatusTypeCode)}
                    type={getInvoiceStatusChipType(invoiceStatusTypeCode)}
                    size="small"
                />
            </div>
            <p className="text-body1">{clientLinks}</p>
            {hasDifferingInvoiceClient && (
                <p className="text-body1">
                    <FormattedMessage
                        id="invoices.payer"
                        values={{
                            client: (
                                <PartyName
                                    key="client"
                                    party={clientParty}
                                    isLink
                                    useLinkWithOrigin
                                    variant="readonly-other"
                                    showPartyNumber
                                />
                            ),
                        }}
                    />
                </p>
            )}
        </div>
    );
};

export const PrepaymentLinkedInvoices = ({
    linkedInvoices,
    currencyTypeCode,
    classifiers,
}: {
    linkedInvoices: LinkedInvoiceMinDto[];
    currencyTypeCode: string;
    classifiers: Record<string, ClassifierData>;
}): JSX.Element => {
    const isNewDesign = useIsNewDesign();
    if (!linkedInvoices || linkedInvoices.length === 0) {
        return <p className="text-body1">-</p>;
    }

    return (
        <div className="flex flex-col gap-0.5">
            {linkedInvoices.map((linked) => {
                const chips = (
                    <>
                        <InvoiceTypeLetterChip
                            typeCode={linked.invoiceTypeCode}
                            typeLabel={getClassifierText(classifiers?.['INVOICE_TYPE'], linked.invoiceTypeCode)}
                        />
                        <StatusChip
                            label={getClassifierText(
                                classifiers?.['INVOICE_STATUS_TYPE'],
                                linked.invoiceStatusTypeCode
                            )}
                            type={getInvoiceStatusChipType(linked.invoiceStatusTypeCode)}
                            size="small"
                        />
                    </>
                );
                const invoice = (
                    <>
                        <RecordLink
                            className={cn(CELL_LINK, 'whitespace-nowrap')}
                            to={`/invoice/${linked.id}`}
                            label={linked.invoiceNumber}
                            withOrigin
                        />
                        <span className="text-caption text-fg-secondary whitespace-nowrap">
                            (<MoneyDisplay amount={Number(linked.grossAmount)} currency={currencyTypeCode} />)
                        </span>
                    </>
                );
                // The new design puts each invoice's chips on a line of their own, under it.
                return isNewDesign ? (
                    <div key={linked.id} className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1">{invoice}</div>
                        <div className="flex items-center gap-1">{chips}</div>
                    </div>
                ) : (
                    <div key={linked.id} className="flex items-center gap-1">
                        {chips}
                        {invoice}
                    </div>
                );
            })}
        </div>
    );
};

export const PrepaymentCaseCell = ({ prepayment }: { prepayment: PrepaymentListItem }): JSX.Element => {
    const { matters } = prepayment;

    if (!matters || matters.length === 0) {
        return <p className="text-body1">-</p>;
    }

    const sortedMatters = matters.toSorted((a, b) => b.id - a.id);

    return (
        <ListClamp
            items={sortedMatters}
            maxVisibleItems={1}
            getKey={({ id }) => id}
            renderItem={(matter) => (
                <RecordLink
                    className={CELL_LINK}
                    to={`/matter/${matter.id}`}
                    label={`${matter.caseNumber} ${matter.name}`}
                >
                    <BoxNowrap>{matter.caseNumber}</BoxNowrap>
                    {matter.name}
                </RecordLink>
            )}
        />
    );
};

export const PrepaymentRetainerBalanceCell = ({ prepayment }: { prepayment: PrepaymentListItem }): JSX.Element => (
    <p className="text-body1">
        <MoneyDisplay amount={Number(prepayment.retainerBalance)} currency={prepayment.baseCurrencyTypeCode} />
    </p>
);

export const PrepaymentAmountCell = ({
    amount,
    currencyTypeCode,
    amountBaseCurrency,
    baseCurrencyTypeCode,
    className,
}: {
    amount: string;
    currencyTypeCode: string;
    amountBaseCurrency?: string;
    baseCurrencyTypeCode?: string;
    /** Classes for the amount on the first line. */
    className?: string;
}): JSX.Element => (
    // The two lines stack via the parent cell's box model, which the DataTable engine drops once
    // virtualized rows switch cells to `display: flex` — an explicit column keeps them stacked either way.
    <div className="flex flex-col">
        <p className={cn('text-body1', className)}>
            <MoneyDisplay amount={Number(amount)} currency={currencyTypeCode} />
        </p>
        {baseCurrencyTypeCode && (
            <span className="text-caption text-fg-secondary">
                <MoneyDisplay amount={Number(amountBaseCurrency ?? 0)} currency={baseCurrencyTypeCode} />
            </span>
        )}
    </div>
);
